import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useCallback, useMemo, useRef, useState } from 'react'
import { MoreHorizontal } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { Sort } from '@/components/data-table/types'
import type { InventoryItem } from '@/api/endpoints/inventory'
import { inventoryApi } from '@/api/endpoints/inventory'
import { inventoryHistoryApi } from '@/api/endpoints/inventory-history'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { useCountSessionGuard } from '@/features/inventory-count/hooks/use-count-session-guard'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { ACTIONS } from '@/shared/constants/actions'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { DataTable } from '@/components/data-table/data-table'
import {
  buildInventoryHistoryColumns,
  resolveOrderIdFromHistory,
} from '@/features/inventory/components/inventory-history-columns'
import {
  buildInventoryEventKindOptions,
  buildInventoryEventTypeOptions,
} from '@/features/inventory/components/inventory-history-options'
import { InventoryHistoryOrderDrawer } from '@/features/inventory/components/inventory-history-order-drawer'
import { stockInfoOf } from '@/features/inventory/hooks/use-stock-info'
import { useProductDetail } from '@/features/products/hooks/use-product-detail'
import {
  addStockQuantities,
  formatStockDisplay,
  parseStockAmount,
  subtractStockQuantities,
} from '@/shared/lib/stock-quantity'

type ActionKind = 'receive' | 'setOnHand' | 'setBuffer'

const HISTORY_QUERY_KEY = 'inventory-history'

export function InventoryTab({ sku }: { sku: string }) {
  const queryClient = useQueryClient()
  const { t: tInventory } = useTranslation('inventory')
  const { t: tCommon } = useTranslation('common')
  const { can } = usePermissions()
  const canReceive = can(ACTIONS.INVENTORY_RECEIVE)
  const canAdjust = can(ACTIONS.INVENTORY_ADJUST)
  const { data, isLoading, error } = useQuery({
    queryKey: ['inventory', sku],
    queryFn: () => inventoryApi.list(sku),
  })

  const { data: restaurantsData } = useRestaurants()
  // CHG-2026-09-stock-usage-and-price-twins (D9, D11): the article's counting mode and unit, and
  // whether it holds stock at all. Until the product is loaded it counts as a whole-number stock
  // article, which is what every product was before the change.
  const { data: product } = useProductDetail(sku)
  const stockInfo = product ? stockInfoOf(product) : null
  const fractional = stockInfo?.fractional === true
  const noStock = stockInfo?.noStock ?? null
  const offersActions = (canReceive || canAdjust) && noStock == null

  const [dialogOpen, setDialogOpen] = useState(false)
  const [action, setAction] = useState<ActionKind>('receive')
  const [selected, setSelected] = useState<InventoryItem | null>(null)
  const [value, setValue] = useState<string>('')
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)

  const totals = useMemo(() => {
    const items = data?.items ?? []
    return items.reduce(
      (acc, it) => {
        const avail = it.availableQty ?? 0
        acc.available = addStockQuantities(acc.available, avail)
        acc.reserved = addStockQuantities(acc.reserved, it.reservedQty)
        acc.onHand = addStockQuantities(acc.onHand, avail, it.reservedQty)
        return acc
      },
      { available: 0, reserved: 0, onHand: 0 },
    )
  }, [data])

  const openDialog = (item: InventoryItem, kind: ActionKind) => {
    setSelected(item)
    setAction(kind)
    setValue('')
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setSelected(null)
    clientRefRef.current = null
  }

  // One key per submission (D6): reused while retrying a submit that timed out, dropped when the
  // dialog closes or the movement is posted.
  const clientRefRef = useRef<string | null>(null)

  const receiveMutation = useMutation({
    mutationFn: async ({
      restaurantId,
      quantity,
    }: {
      restaurantId: number
      quantity: number
    }) => {
      if (!clientRefRef.current) clientRefRef.current = newIdempotencyKey()
      return inventoryApi.receive(
        sku,
        restaurantId,
        quantity,
        clientRefRef.current,
      )
    },
    onSuccess: () => {
      clientRefRef.current = null
      toast.success(tInventory('toast.inventoryReceived'))
      queryClient.invalidateQueries({ queryKey: ['inventory', sku] })
      queryClient.invalidateQueries({ queryKey: [HISTORY_QUERY_KEY] })
      closeDialog()
    },
    onError: () => toast.error(tInventory('toast.inventoryReceiveFailed')),
  })

  const setOnHandMutation = useMutation({
    mutationFn: async ({
      restaurantId,
      onHandQty,
      reservedQty,
    }: {
      restaurantId: number
      onHandQty: number
      reservedQty: number
    }) =>
      inventoryApi.setAvailable(
        sku,
        restaurantId,
        // Thousandths only where decimals are allowed: a whole-number article sends exactly what
        // it always sent.
        fractional
          ? subtractStockQuantities(onHandQty, reservedQty)
          : onHandQty - reservedQty,
      ),
    onSuccess: () => {
      toast.success(tInventory('toast.onHandUpdated'))
      queryClient.invalidateQueries({ queryKey: ['inventory', sku] })
      queryClient.invalidateQueries({ queryKey: [HISTORY_QUERY_KEY] })
      closeDialog()
    },
    onError: () => toast.error(tInventory('toast.onHandUpdateFailed')),
  })

  const setBufferMutation = useMutation({
    mutationFn: async ({
      restaurantId,
      qty,
    }: {
      restaurantId: number
      qty: number
    }) => inventoryApi.setSafetyBuffer(sku, restaurantId, qty),
    onSuccess: () => {
      toast.success(tInventory('toast.bufferUpdated'))
      queryClient.invalidateQueries({ queryKey: ['inventory', sku] })
      closeDialog()
    },
    onError: () => toast.error(tInventory('toast.bufferUpdateFailed')),
  })
  const restaurantsById = (restaurantsData?.items ?? []).reduce<
    Record<number, string>
  >((acc, r) => {
    acc[r.id] = r.name
    return acc
  }, {})
  const handleSelectOrder = useCallback((orderId: string) => {
    setSelectedOrderId(orderId)
  }, [])

  const historyColumns = buildInventoryHistoryColumns(
    tInventory,
    restaurantsById,
    {
      onSelectOrder: handleSelectOrder,
    },
  )
  const restaurantOptions = [
    { label: tInventory('timeline.filters.allRestaurants'), value: 'all' },
  ].concat(
    (data?.items ?? []).map((it) => ({
      label: it.restaurantName,
      value: String(it.restaurantId),
    })),
  )
  const eventKindOptions = buildInventoryEventKindOptions(tInventory)
  const eventTypeOptions = buildInventoryEventTypeOptions(tInventory)

  async function fetchHistory({
    pageIndex,
    pageSize,
    sorting: _sorting,
    filters,
  }: {
    pageIndex: number
    pageSize: number
    sorting?: Array<Sort>
    filters?: Record<string, unknown>
  }) {
    const restaurantFilter =
      typeof filters?.restaurantId === 'string'
        ? filters.restaurantId
        : undefined
    const restaurantId =
      restaurantFilter && restaurantFilter !== 'all'
        ? Number(restaurantFilter)
        : undefined
    const kindFilter =
      typeof filters?.eventKind === 'string' ? filters.eventKind : undefined
    const eventKind =
      kindFilter === 'movement' || kindFilter === 'reservation'
        ? kindFilter
        : 'all'
    const typeFilter =
      typeof filters?.eventType === 'string' ? filters.eventType : undefined
    const eventType =
      typeFilter && typeFilter !== 'all' ? typeFilter : undefined
    const res = await inventoryHistoryApi.list({
      sku,
      restaurantId,
      eventKind,
      eventType,
      page: pageIndex,
      pageSize,
    })
    return {
      rows: res.items,
      total: res.total,
      pageCount: Math.ceil(res.total / res.pageSize),
    }
  }

  const countGuard = useCountSessionGuard(selected?.restaurantId ?? null)

  const isMutating =
    receiveMutation.isPending ||
    setOnHandMutation.isPending ||
    setBufferMutation.isPending ||
    countGuard.checking

  const submit = () => {
    if (!selected) return
    const canAction = action === 'receive' ? canReceive : canAdjust
    if (!canAction) return
    // "Teilmengen zählen": the de-AT comma and up to 3 decimals; a whole-number article keeps the
    // rule it always had.
    const typed = fractional ? parseStockAmount(value) : null
    const n = typed ? (typed.ok ? typed.value : Number.NaN) : Number(value)
    if (!Number.isFinite(n) || n < 0 || (action === 'receive' && n === 0)) {
      toast.error(tInventory('toast.invalidNumber'))
      return
    }
    if (action === 'receive') {
      void countGuard.guard(() =>
        receiveMutation.mutate({
          restaurantId: selected.restaurantId,
          quantity: n,
        }),
      )
    } else if (action === 'setOnHand') {
      void countGuard.guard(() =>
        setOnHandMutation.mutate({
          restaurantId: selected.restaurantId,
          onHandQty: n,
          reservedQty: selected.reservedQty,
        }),
      )
    } else {
      setBufferMutation.mutate({ restaurantId: selected.restaurantId, qty: n })
    }
  }

  return (
    <div className="space-y-6">
      {/* Summary strip */}
      <div className="flex gap-6">
        <div>
          <div className="text-sm text-muted-foreground">
            {tInventory('table.onHand')}
          </div>
          <div className="text-lg font-semibold font-mono">
            {formatStockDisplay(totals.onHand)}
          </div>
        </div>
        <div>
          <div className="text-sm text-muted-foreground">
            {tInventory('table.available')}
          </div>
          <div className="text-lg font-semibold font-mono">
            {formatStockDisplay(totals.available)}
          </div>
        </div>
        <div>
          <div className="text-sm text-muted-foreground">
            {tInventory('table.reserved')}
          </div>
          <div className="text-lg font-semibold font-mono">
            {formatStockDisplay(totals.reserved)}
          </div>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        {tInventory('summary.onHandFormula')}
      </p>

      {(noStock === 'dish' || noStock === 'not_tracked') && (
        <p
          role="note"
          className="rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground"
        >
          {tInventory(`productTab.noStock.${noStock}`)}
        </p>
      )}

      {/* Inventory levels table */}
      {isLoading && (
        <div className="py-10 text-center text-sm text-muted-foreground">
          {tInventory('status.loading')}
        </div>
      )}
      {error && (
        <div className="py-10 text-center text-sm text-red-600">
          {tInventory('status.loadFailed')}
        </div>
      )}
      {!isLoading && data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-2 pr-4 font-medium">
                  {tInventory('table.restaurant')}
                </th>
                <th className="py-2 pr-4 font-medium">
                  {tInventory('table.available')}
                </th>
                <th className="py-2 pr-4 font-medium">
                  {tInventory('table.reserved')}
                </th>
                <th className="py-2 pr-4 font-medium">
                  {tInventory('table.onHand')}
                </th>
                <th className="py-2 pr-4 font-medium">
                  {tInventory('table.safetyBuffer')}
                </th>
                <th className="py-2 pr-4 font-medium">
                  {tInventory('table.updated')}
                </th>
                <th className="py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.restaurantId} className="border-b last:border-0">
                  <td className="py-2 pr-4">{it.restaurantName}</td>
                  <td className="py-2 pr-4 font-mono">
                    {formatStockDisplay(it.availableQty ?? 0)}
                  </td>
                  <td className="py-2 pr-4 font-mono">
                    {formatStockDisplay(it.reservedQty)}
                  </td>
                  <td className="py-2 pr-4 font-mono">
                    {formatStockDisplay(
                      addStockQuantities(it.availableQty, it.reservedQty),
                    )}
                  </td>
                  <td className="py-2 pr-4 font-mono">
                    {formatStockDisplay(it.safetyBufferQty ?? 0)}
                  </td>
                  <td className="py-2 pr-4 text-muted-foreground">
                    {it.updatedAt
                      ? new Date(it.updatedAt).toLocaleString()
                      : '-'}
                  </td>
                  <td className="py-2">
                    {offersActions && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canReceive && (
                            <DropdownMenuItem
                              onClick={() => openDialog(it, 'receive')}
                            >
                              {tInventory('actions.receive')}
                            </DropdownMenuItem>
                          )}
                          {canAdjust && (
                            <>
                              <DropdownMenuItem
                                onClick={() => openDialog(it, 'setOnHand')}
                              >
                                {tInventory('actions.setOnHand')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openDialog(it, 'setBuffer')}
                              >
                                {tInventory('actions.setBuffer')}
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* History section */}
      <Separator />

      <div>
        <h3 className="text-sm font-medium mb-3">
          {tInventory('timeline.title')}
        </h3>
        <DataTable
          queryKey={HISTORY_QUERY_KEY}
          columns={historyColumns}
          fetcher={fetchHistory}
          filters={[
            {
              id: 'restaurantId',
              label: tInventory('timeline.filters.restaurant'),
              type: 'select',
              options: restaurantOptions,
            },
            {
              id: 'eventKind',
              label: tInventory('timeline.filters.kind'),
              type: 'select',
              options: eventKindOptions,
            },
            {
              id: 'eventType',
              label: tInventory('timeline.filters.type'),
              type: 'select',
              options: eventTypeOptions,
            },
          ]}
          globalSearch={false}
          selectable={false}
          enableColumnVisibility
          showDensityToggle={false}
          initialState={{ pageSize: 20 }}
          getRowId={(row) =>
            row.clientRef || String(row.movementId ?? row.orderId ?? row.sku)
          }
          onRowClick={(row) => {
            const orderId = resolveOrderIdFromHistory(row.original)
            if (orderId) setSelectedOrderId(orderId)
          }}
        />
      </div>

      <InventoryHistoryOrderDrawer
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
        restaurantNameById={restaurantsById}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {action === 'receive' && tInventory('dialog.receiveTitle')}
              {action === 'setOnHand' && tInventory('dialog.setOnHandTitle')}
              {action === 'setBuffer' && tInventory('dialog.setBufferTitle')}
            </DialogTitle>
            <DialogDescription>
              {selected ? `${selected.restaurantName} — ${sku}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="qty">
              {action === 'receive'
                ? tInventory('dialog.quantityReceive')
                : action === 'setOnHand'
                  ? tInventory('dialog.quantityOnHand')
                  : tInventory('dialog.quantityBuffer')}
            </Label>
            {stockInfo?.fractional ? (
              // A number field drops the de-AT comma; "Teilmengen zählen" needs a text field.
              <>
                <Input
                  id="qty"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
                <p className="text-sm text-muted-foreground">
                  {tInventory('dialog.decimalHint', { unit: stockInfo.unit })}
                </p>
              </>
            ) : (
              <Input
                id="qty"
                type="number"
                inputMode="numeric"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            )}
            {action === 'setOnHand' && selected && (
              <p className="text-sm text-muted-foreground">
                {tInventory('dialog.onHandHint', {
                  reserved: formatStockDisplay(selected.reservedQty),
                })}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={isMutating}
            >
              {tCommon('cancel')}
            </Button>
            <Button
              onClick={submit}
              disabled={
                isMutating || (action === 'receive' ? !canReceive : !canAdjust)
              }
            >
              {isMutating && <Spinner className="w-4 h-4 mr-2" />}
              {tCommon('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {countGuard.dialog}
    </div>
  )
}
